import type { DocumentNode, FetchPolicy } from '@apollo/client';
import { getDefaultGraphqlClient } from '@core/api/graphqlClient';
import { apolloContextForShop } from '@core/api/apolloContext';
import { CATALOG_FETCH_POLICY } from '@core/api/apolloQueryOptions';

export type FetchPerShopOptions = {
  shopIds: string[];
  query: DocumentNode;
  variables?: (shopId: string) => Record<string, unknown>;
  fetchPolicy?: FetchPolicy;
};

export type FetchPerShopResult<T> = {
  status: 'fulfilled' | 'rejected';
  shopId: string;
  data?: T;
  error?: unknown;
};

/**
 * Parallel per-shop GraphQL reads with optional session cache and allSettled semantics.
 */
export async function fetchPerShop<TData>(
  options: FetchPerShopOptions
): Promise<FetchPerShopResult<TData>[]> {
  const {
    shopIds,
    query,
    variables = shopId => ({ shopId }),
    fetchPolicy = CATALOG_FETCH_POLICY,
  } = options;

  if (!shopIds.length) return [];

  const client = getDefaultGraphqlClient();
  const settled = await Promise.allSettled(
    shopIds.map(async shopId => {
      const res = await client.query<TData>({
        query,
        variables: variables(shopId),
        fetchPolicy,
        context: apolloContextForShop(shopId),
      });
      if (res.error) {
        throw res.error;
      }
      return res.data as TData;
    })
  );

  const results: FetchPerShopResult<TData>[] = settled.map((result, i) => {
    const shopId = shopIds[i]!;
    if (result.status === 'fulfilled') {
      return { status: 'fulfilled', shopId, data: result.value };
    }
    return { status: 'rejected', shopId, error: result.reason };
  });

  return results;
}
